import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import { ProducerProductLink } from '@/model/ProducerProductLinkDTO';
import { toast } from 'react-toastify';
import { SectionProductProducerGroup } from '@/model/SectionProductProducerGroup';
import { unmask, updateMask } from '@/helpers/masks/cashNumber';
import { ProducerProductDetailContainer } from './ui';
import { ChangeType } from '../../types';

export const ProducerProductDetailScreen: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useHistory();

  const [state, setState] = useState<States>(States.default);
  const [producerProductLink, setProducerProductLink] = useState<ProducerProductLink>();
  const [changed, setChanged] = useState<boolean>(false);

  const handleOnGetProducerProductProducts = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<ProducerProductLink>(
        `/support/producer/product/validate/${id}`,
      );

      if (data && data.groups && data.groups.length > 0) {
        const newProducerProductLink = { ...data };
        // eslint-disable-next-line no-plusplus
        for (let g = 0; g < newProducerProductLink.groups.length; g++) {
          // eslint-disable-next-line no-plusplus
          for (let p = 0; p < newProducerProductLink.groups[g].products.length; p++) {
            newProducerProductLink.groups[g].products[p].price = updateMask(
              newProducerProductLink.groups[g].products[p].price as unknown as string,
            ) as unknown as number;
          }
        }
        setProducerProductLink(newProducerProductLink);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnChange = (
    changeType: ChangeType,
    groupIx: number,
    productIx: number,
    value: string,
  ): void => {
    const newProducerProductLink = { ...producerProductLink, groups: [] } as ProducerProductLink;
    if (producerProductLink) {
      setChanged(true);
      // eslint-disable-next-line no-plusplus
      for (let g = 0; g < producerProductLink.groups.length; g++) {
        if (g === groupIx) {
          const newGroup = {
            ...producerProductLink.groups[g],
            products: [],
          } as SectionProductProducerGroup;
          // eslint-disable-next-line no-plusplus
          for (let p = 0; p < producerProductLink.groups[g].products.length; p++) {
            if (p === productIx) {
              const newProdcut = { ...producerProductLink.groups[g].products[p] };
              if (changeType === ChangeType.PRODUCT_NAME) {
                newProdcut.name = value;
              } else if (changeType === ChangeType.PRODUCT_PRICE) {
                newProdcut.price = updateMask(value) as unknown as number;
              }
              newGroup.products.push(newProdcut);
            } else {
              newGroup.products.push(producerProductLink.groups[g].products[p]);
            }
          }
          newProducerProductLink.groups.push(newGroup);
        } else {
          newProducerProductLink.groups.push(producerProductLink.groups[g]);
        }
      }
    }
    setProducerProductLink(newProducerProductLink);
  };

  const handleOnValidate = async (): Promise<void> => {
    try {
      setState(States.loading);
      if (changed) {
        if (producerProductLink) {
          const payload: SectionProductProducerGroup[] = [...producerProductLink.groups];

          // eslint-disable-next-line no-plusplus
          for (let g = 0; g < payload.length; g++) {
            // eslint-disable-next-line no-plusplus
            for (let p = 0; p < payload[g].products.length; p++) {
              payload[g].products[p].price = unmask(
                payload[g].products[p].price as unknown as string,
              ) as unknown as number;
            }
          }
          await api.post(`/support/producer/product/${id}/validate`, payload);
        }
      } else {
        await api.patch(`/support/producer/product/${id}/validate`);
      }

      toast.success('Produtos validados com sucesso');
      navigate.replace(`/support/producer-product`);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnRefused = async (): Promise<void> => {
    try {
      setState(States.loading);
      await api.patch(`/support/producer/product/${id}/refuse`);
      toast.success('Produtos recusados com sucesso');
      navigate.replace(`/support/producer-product`);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  useEffect(() => {
    handleOnGetProducerProductProducts();
  }, [id]);
  return (
    <ProducerProductDetailContainer
      state={state}
      producerProductLink={producerProductLink}
      onChange={handleOnChange}
      onRefused={handleOnRefused}
      onValidate={handleOnValidate}
    />
  );
};
